import { useState, useEffect } from "react";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Loader2, Database, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import QueryEditor from "./QueryEditor";

const WIDGET_TYPES = [
  { id: "stat", label: "Stat Card" },
  { id: "bar", label: "Bar Chart" },
  { id: "line", label: "Line Chart" },
  { id: "area", label: "Area Chart" },
  { id: "pie", label: "Pie Chart" },
  { id: "table", label: "Table" },
] as const;

type WidgetTypeId = (typeof WIDGET_TYPES)[number]["id"];

interface EditWidgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  widget: Record<string, any> | null;
  widgetIndex?: number | null;
  dashboardId?: string | null;
  onSave: (updates: {
    title: string;
    type: string;
    sqlQuery?: string;
    _queryName?: string;
    xKey?: string;
    yKeys?: string[];
    width?: number;
    height?: number;
  }) => void;
  onDelete: () => void;
}

/** Parse a raw execute/queries response into an array of row objects. */
const parseRows = (raw: unknown): Record<string, unknown>[] => {
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (Array.isArray(first)) return first as Record<string, unknown>[];
    if (first && typeof first === "object" && ("rows" in first || "result" in first)) {
      return ((first as any).rows || (first as any).result || []) as Record<string, unknown>[];
    }
    return raw as Record<string, unknown>[];
  }
  if (raw && typeof raw === "object") {
    const r = raw as any;
    if (r.results) {
      const first = Array.isArray(r.results) ? r.results[0] : r.results;
      return Array.isArray(first) ? first : (first?.rows || first?.result || []);
    }
    if (r.rows) return r.rows;
  }
  return [];
};

const EditWidgetDialog = ({ open, onOpenChange, widget, widgetIndex, dashboardId, onSave, onDelete }: EditWidgetDialogProps) => {
  const [title, setTitle] = useState("");
  const [type, setType] = useState<WidgetTypeId>("stat");
  const [queryName, setQueryName] = useState("");
  const [selectedQuerySql, setSelectedQuerySql] = useState("");
  const [xKey, setXKey] = useState("");
  const [yKeys, setYKeys] = useState("");
  const [widgetWidth, setWidgetWidth] = useState("6");
  const [widgetHeight, setWidgetHeight] = useState("300");

  // Column autocomplete
  const [queryColumns, setQueryColumns] = useState<string[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [xKeyFocused, setXKeyFocused] = useState(false);
  const [queryEditorOpen, setQueryEditorOpen] = useState(false);

  useEffect(() => {
    if (widget && open) {
      setTitle(widget.title || "");
      setType((widget.type as WidgetTypeId) || "stat");
      setQueryName(widget._queryName || "");
      setSelectedQuerySql(widget.sqlQuery || "");
      setXKey(widget.xKey || "");
      setYKeys(widget.yKeys?.join(", ") || "");
      setWidgetWidth(widget.width ? String(widget.width) : "6");
      setWidgetHeight(widget.height ? String(widget.height) : "300");
    }
  }, [widget, open]);

  // Fetch columns when selected query changes
  useEffect(() => {
    if (!selectedQuerySql) {
      setQueryColumns([]);
      return;
    }
    let cancelled = false;
    const fetchColumns = async () => {
      setLoadingColumns(true);
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const headers = await buildEdgeFunctionHeaders();
        const sql = selectedQuerySql.trim().replace(/;\s*$/, "");
        const wrappedSql = /\bLIMIT\b/i.test(sql) ? sql : `${sql} LIMIT 1`;
        const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
          method: "POST",
          headers,
          body: JSON.stringify([wrappedSql]),
        });
        const json = await res.json();
        const rows = parseRows(json);
        if (!cancelled && rows.length > 0) {
          setQueryColumns(Object.keys(rows[0]));
        } else if (!cancelled) {
          setQueryColumns([]);
        }
      } catch (err) {
        console.error("Failed to fetch query columns:", err);
        if (!cancelled) setQueryColumns([]);
      } finally {
        if (!cancelled) setLoadingColumns(false);
      }
    };
    fetchColumns();
    return () => { cancelled = true; };
  }, [selectedQuerySql]);

  const handleEditQuery = () => {
    setQueryEditorOpen(true);
  };

  const handleQuerySaved = (query: { id: string; name: string; sql_query: string }) => {
    setQueryName(query.name || "");
    setSelectedQuerySql(query.sql_query);
    setQueryEditorOpen(false);
  };

  const needsAxisConfig = ["bar", "line", "area"].includes(type);

  const yKeysArray = yKeys.split(",").map((k) => k.trim()).filter(Boolean);

  const toggleYKey = (col: string) => {
    if (yKeysArray.includes(col)) {
      setYKeys(yKeysArray.filter((k) => k !== col).join(", "));
    } else {
      setYKeys([...yKeysArray, col].join(", "));
    }
  };

  const filteredXColumns = queryColumns.filter(
    (c) => !xKey || c.toLowerCase().includes(xKey.toLowerCase())
  );

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      type,
      sqlQuery: selectedQuerySql || undefined,
      _queryName: queryName || undefined,
      xKey: needsAxisConfig ? xKey || undefined : undefined,
      yKeys: needsAxisConfig && yKeys
        ? yKeys.split(",").map((k) => k.trim()).filter(Boolean)
        : undefined,
      width: parseInt(widgetWidth),
      height: parseInt(widgetHeight),
    });
    onOpenChange(false);
  };

  const handleDelete = () => {
    onDelete();
    onOpenChange(false);
  };

  if (!widget) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Widget</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label>Widget Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Width (columns)</Label>
              <Select value={widgetWidth} onValueChange={setWidgetWidth}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 — Quarter</SelectItem>
                  <SelectItem value="4">4 — Third</SelectItem>
                  <SelectItem value="6">6 — Half</SelectItem>
                  <SelectItem value="8">8 — Two-thirds</SelectItem>
                  <SelectItem value="12">12 — Full</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Height (px)</Label>
              <Select value={widgetHeight} onValueChange={setWidgetHeight}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="200">200 — Small</SelectItem>
                  <SelectItem value="300">300 — Medium</SelectItem>
                  <SelectItem value="400">400 — Large</SelectItem>
                  <SelectItem value="500">500 — X-Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Widget Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as WidgetTypeId)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WIDGET_TYPES.map((wt) => (
                  <SelectItem key={wt.id} value={wt.id}>
                    {wt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Data Source</Label>
            {selectedQuerySql ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
                  <Database className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{queryName || "Inline Query"}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEditQuery}
                  >
                    <Pencil className="mr-1 h-3 w-3" /> Edit
                  </Button>
                </div>
                <pre className="overflow-auto max-h-[60px] rounded-md bg-muted p-2 text-[10px] font-mono text-muted-foreground whitespace-pre-wrap">
                  {selectedQuerySql.slice(0, 200)}{selectedQuerySql.length > 200 ? "…" : ""}
                </pre>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start text-muted-foreground"
                onClick={handleEditQuery}
              >
                <Database className="mr-2 h-4 w-4" />
                Create Query
              </Button>
            )}
            {!selectedQuerySql && (
              <p className="text-[10px] text-muted-foreground">
                No query set. Create a query to provide data for this widget.
              </p>
            )}
          </div>
          {needsAxisConfig && (
            <>
              <div className="grid gap-1.5">
                <Label>
                  X-Axis Column
                  {loadingColumns && (
                    <Loader2 className="inline ml-2 h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </Label>
                <div className="relative">
                  <Input
                    value={xKey}
                    onChange={(e) => setXKey(e.target.value)}
                    onFocus={() => setXKeyFocused(true)}
                    onBlur={() => setTimeout(() => setXKeyFocused(false), 150)}
                    placeholder={
                      queryColumns.length > 0
                        ? "Type or select column…"
                        : "e.g. month, date, category"
                    }
                  />
                  {xKeyFocused && filteredXColumns.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-32 overflow-y-auto">
                      {filteredXColumns.map((col) => (
                        <button
                          key={col}
                          type="button"
                          className={cn(
                            "w-full px-3 py-1.5 text-sm text-left hover:bg-accent",
                            col === xKey && "bg-accent font-medium"
                          )}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setXKey(col);
                            setXKeyFocused(false);
                          }}
                        >
                          {col}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label>Y-Axis Keys</Label>
                <Input
                  value={yKeys}
                  onChange={(e) => setYKeys(e.target.value)}
                  placeholder={
                    queryColumns.length > 0
                      ? "Click columns below or type comma-separated…"
                      : "e.g. revenue, cost (comma-separated)"
                  }
                />
                {queryColumns.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {queryColumns.map((col) => {
                      const selected = yKeysArray.includes(col);
                      return (
                        <button
                          key={col}
                          type="button"
                          onClick={() => toggleYKey(col)}
                          className={cn(
                            "text-xs px-2 py-0.5 rounded-full border transition-colors",
                            selected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-muted/50 hover:bg-accent border-border"
                          )}
                        >
                          {col}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
          {type === "pie" && (
            <p className="text-xs text-muted-foreground">
              Pie chart expects query results with "name" and "value" columns.
            </p>
          )}
          {type === "stat" && (
            <p className="text-xs text-muted-foreground">
              Stat card will display the first value from the query result.
            </p>
          )}
        </div>
        <DialogFooter className="flex !justify-between gap-2">
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!title.trim()}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Inline Query Editor */}
      <Dialog
        open={queryEditorOpen}
        onOpenChange={(o) => { if (!o) setQueryEditorOpen(false); }}
      >
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col">
          {queryEditorOpen && (
            <QueryEditor
              editingQuery={selectedQuerySql ? { sql_query: selectedQuerySql, name: queryName, generation_mode: "manual" } : null}
              onClose={() => setQueryEditorOpen(false)}
              onSaved={handleQuerySaved}
              saveToDb={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default EditWidgetDialog;
